import { ReactNode, useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";

import { Delete, UploadFile } from "@mui/icons-material";
import {
  Button,
  Card,
  CardActionArea,
  FormHelperText,
  Grow,
  Stack,
  Typography,
} from "@mui/material";
import { get } from "lodash";
import { useTranslate } from "../../hooks/useTranslate";

interface IImageInput {
  label: ReactNode;
  name: string;
}

const ImageInput: React.FC<IImageInput> = ({ name, label }) => {
  const form = useFormContext();
  const { translate } = useTranslate();

  const [preview, setPreview] = useState<string | undefined>(() =>
    form.watch(name, undefined)
  );

  const setPreviewImage = useCallback(
    (file: File) => {
      const image = URL.createObjectURL(file);
      form.setValue(name, file);
      setPreview(image);
    },
    [form, name]
  );

  const error = get(form.formState.errors, name);

  return (
    <>
      <Card
        square
        elevation={0}
        sx={(theme) => ({
          bgcolor: "grey.50",
          backgroundImage: `url(${preview})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "contain",
          borderRadius: 0.3,
        })}
      >
        <CardActionArea
          component="label"
          sx={{
            height: 300,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <input
            accept="image/*"
            hidden
            onChange={async (event: any) => {
              const [file] = event.target.files;
              if (file) {
                setPreviewImage(file);
                form.trigger(name);
              }
            }}
            type="file"
          />
          <Grow in={!!preview} unmountOnExit>
            <Button
              color="error"
              onClick={() => {
                form.setValue(name, undefined);
                setPreview(undefined);
              }}
              variant="contained"
            >
              <Delete />
            </Button>
          </Grow>
          <Grow in={!preview} unmountOnExit>
            <Stack alignItems="center" spacing={2}>
              <UploadFile fontSize="large" sx={{ color: "grey.600" }} />
              <Typography variant="caption">
                {translate("animals.select_image")}
              </Typography>
            </Stack>
          </Grow>
        </CardActionArea>
      </Card>
      {error?.message && (
        <FormHelperText sx={{ color: (theme) => theme.palette.error.main }}>
          {String(error?.message)}
        </FormHelperText>
      )}
    </>
  );
};

export default ImageInput;
